import { read, utils } from 'xlsx';
import ExcelJS from 'exceljs';
import { translations } from '../i18n/translations';
import { normalizeStandardCable, convertAWGToMetric, convertMetricToAWG } from './cableLogic';

import { generateACPowerData } from './acPowerLogic';
import { normalizeColorToCanonical } from './colorUtils';
import { createCatalogAssignment, reconcileCablesWithCatalog } from './cableCatalog';

// Le sezioni sono SEMPRE memorizzate in mm² internamente. Se la colonna
// importata è in AWG, convertiamo davvero il calibro nel valore metrico.
const parseImportedSection = (rawValue, isAWG, fallback = 0.5) => {
  const num = parseFloat(rawValue);
  if (Number.isNaN(num)) return fallback;
  return isAWG ? convertAWGToMetric(num) : num;
};
// import { getSortedCablesForList } from '../components/visualization/UnifiedCablesList'; // This will be passed as a prop

// ==================== IMPORT FUNCTIONS ====================

const VALID_SORT_MODES = new Set([
  'numerazione',
  'siglatura',
  'colore',
  'sezione',
  'coloreSezione',
  'gruppo'
]);

const normalizeImportedSortMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return null;
  if (normalized === 'asc' || normalized === 'desc') return 'numerazione';
  if (normalized === 'coloresezione' || normalized === 'colore/sezione') return 'coloreSezione';
  if (VALID_SORT_MODES.has(normalized)) return normalized;

  return null;
};

const WINDOWS_INVALID_FILE_NAME_CHARS_REGEX = /[\\/:*?"<>|]/;
const WINDOWS_RESERVED_FILE_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
]);

export const validateWindowsFileName = (value) => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return { isValid: false, reason: 'empty' };
  }

  if (WINDOWS_INVALID_FILE_NAME_CHARS_REGEX.test(normalized)) {
    return { isValid: false, reason: 'invalid_chars' };
  }

  if (/[. ]$/.test(normalized)) {
    return { isValid: false, reason: 'trailing_char' };
  }

  const basename = normalized.replace(/\.(xlsx?|pdf)$/i, '').toUpperCase();
  if (WINDOWS_RESERVED_FILE_NAMES.has(basename)) {
    return { isValid: false, reason: 'reserved_name' };
  }

  return { isValid: true, normalized };
};

const importSettings = (worksheet, setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode) => {
  const parseBooleanSetting = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;

    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'si' || normalized === 'sì';
  };

  const labelMap = {
    'harness quantity': 'quantitaCablaggi',
    'quantita cablaggi': 'quantitaCablaggi',
    'quantità cablaggi': 'quantitaCablaggi',
    'mark cables': 'marcaCavi',
    'marcare cavi': 'marcaCavi',
    'order reference': 'commessaId',
    'riferimento commessa': 'commessaId',
    'order date': 'commessaDate',
    'data commessa': 'commessaDate',
    'revision': 'revision',
    'electrical schematic': 'schemaElettrico',
    'schema elettrico': 'schemaElettrico',
    'installation': 'installazione',
    'installazione': 'installazione',
    'file name': 'fileName',
    'use awg': 'useAWG',
    'marking by connection': 'marcaturePerCollegamento',
    'marcature per collegamento': 'marcaturePerCollegamento',
    'sort order': 'listSortMode',
    'ordine': 'listSortMode',
    'ordinamento': 'listSortMode'
  };

  const normalizeLabel = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const importedSettings = {};

  const rows = utils.sheet_to_json(worksheet, { header: 1, raw: true });

  if (rows.length > 1) {
    rows.slice(1).forEach((row) => {
      const label = normalizeLabel(row[0]);
      const key = labelMap[label];
      if (key !== undefined) {
        importedSettings[key] = row[1];
      }
    });
  }

  if (Object.keys(importedSettings).length === 0) {
    const fallbackReaders = [
      ['B2', 'quantitaCablaggi'],
      ['B3', 'commessaId'],
      ['B4', 'commessaDate'],
      ['B3', 'marcaCavi'],
      ['B18', 'revision'],
      ['B19', 'schemaElettrico'],
      ['B20', 'installazione'],
      ['B22', 'fileName'],
      ['B23', 'useAWG'],
      ['B20', 'revision'],
      ['B21', 'schemaElettrico'],
      ['B22', 'installazione'],
      ['B24', 'fileName'],
      ['B25', 'useAWG']
    ];

    fallbackReaders.forEach(([cell, key]) => {
      if (worksheet[cell] && importedSettings[key] === undefined) {
        importedSettings[key] = worksheet[cell].v;
      }
    });
  }

  Object.keys(importedSettings).forEach((key) => {
    const value = importedSettings[key];

    if (key === 'listSortMode') {
      importedSettings[key] = normalizeImportedSortMode(value) || 'numerazione';
    } else if (['schemaElettrico', 'installazione', 'fileName', 'commessaId', 'commessaDate'].includes(key)) {
      importedSettings[key] = String(value || '');
    } else if (['useAWG', 'marcaturePerCollegamento'].includes(key)) {
      importedSettings[key] = parseBooleanSetting(value);
    } else if (key === 'revision') {
      importedSettings[key] = parseInt(value, 10) || 0;
    } else {
      importedSettings[key] = parseInt(value, 10) || 0;
    }
  });

  // Impostazione di progetto: i file esportati prima della sua introduzione non
  // la contengono, quindi va azzerata per non ereditarla dal progetto precedente.
  if (importedSettings.marcaturePerCollegamento === undefined) {
    importedSettings.marcaturePerCollegamento = false;
  }

  setSettings(prev => ({ ...prev, ...importedSettings }));

  if (importedSettings.revision !== undefined) setRevision(Math.max(0, Math.min(99, parseInt(importedSettings.revision) || 0)));
  if (importedSettings.schemaElettrico !== undefined) setSchemaElettrico(importedSettings.schemaElettrico);
  if (importedSettings.installazione !== undefined) setInstallazione(importedSettings.installazione);
  if (importedSettings.fileName !== undefined) setFileName(importedSettings.fileName);
  if (typeof setListSortMode === 'function') {
    setListSortMode(importedSettings.listSortMode || 'numerazione');
  }

  return importedSettings;
};

export const importWiringTableData = (worksheet, availableSheaths = [], fileUseAWG = undefined) => {
  const normalizeHeader = (header) => String(header || '').replace(/\./g, '').trim().toLowerCase();

  const headerMap = {
    n: 'N', gruppo: 'Gruppo', siglatura: 'Siglatura', 'siglatura a': 'Siglatura A', 'siglatura b': 'Siglatura B', colore: 'Colore',
    'sez [mmq]': 'Sez [mmq]', 'sez mmq': 'Sez [mmq]',
    'sez [awg]': 'Sez [mmq]', 'sez awg': 'Sez [mmq]',
    'lung [mm]': 'Lung [mm]', 'lung mm': 'Lung [mm]',
    'term a': 'TERM A', 'term b': 'TERM B',
    'colleg a': 'COLLEG A', 'colleg b': 'COLLEG B',
    'guaina': 'Guaina',
    'codice interno': 'Codice interno', '_catalog id': '_Catalog ID'
  };

  const raw = utils.sheet_to_json(worksheet, { header: 1 });
  if (raw.length === 0) return [];

  const headers = raw[0].map(h => normalizeHeader(h));
  // Fonte di verità: il toggle "Use AWG" dello sheet Settings (retrocompatibile
  // con i file salvati prima dell'introduzione dell'header "Sez [AWG]"). Se il
  // foglio Settings manca, ricadiamo sul rilevamento dall'header della colonna.
  const headerIsAWG = headers.some(h => h === 'sez [awg]' || h === 'sez awg');
  const isAWGSection = fileUseAWG !== undefined ? fileUseAWG : headerIsAWG;
  const mappedHeaders = headers.map(h => headerMap[h] || h);

  const jsonData = raw.slice(1).map(row => {
    const obj = {};
    mappedHeaders.forEach((key, idx) => {
      obj[key] = row[idx];
    });
    return obj;
  }).filter(row => {
    let numValue = row['N'];
    if (typeof numValue === 'string' && numValue.startsWith('#')) {
      numValue = numValue.substring(1);
    }
    const hasValidNumber = numValue && !isNaN(parseInt(numValue));
    const hasAnyContent = Object.values(row).some(value =>
      value !== undefined && value !== null && String(value).trim() !== ''
    );
    const hasTerminals = row['TERM A'] && row['TERM B'];

    return hasValidNumber && hasAnyContent && hasTerminals;
  });

  return jsonData.map(row => {
    let numValue = row['N'];
    if (typeof numValue === 'string' && numValue.startsWith('#')) {
      numValue = numValue.substring(1);
    }

    // Gestione guaina: #1 → ID della prima guaina nell'array
    let sheathId = null;
    if (row['Guaina'] && availableSheaths.length > 0) {
      const sheathRef = String(row['Guaina']).trim();
      if (sheathRef.startsWith('#')) {
        const sheathIndex = parseInt(sheathRef.substring(1)) - 1;
        if (sheathIndex >= 0 && sheathIndex < availableSheaths.length) {
          sheathId = availableSheaths[sheathIndex].id;
        }
      }
    }

    const catalogAssignment = createCatalogAssignment(row['_Catalog ID'], row['Codice interno']);
    return normalizeStandardCable({
      originalIndex: parseInt(numValue) - 1,
      gruppo: row['Gruppo'] || '',
      siglaturaA: row['Siglatura A'] ?? row['Siglatura'] ?? '',
      siglaturaB: row['Siglatura B'] ?? row['Siglatura'] ?? '',
      useSeparateSiglature: Boolean(
        row['Siglatura A'] !== undefined &&
        row['Siglatura B'] !== undefined &&
        String(row['Siglatura A'] || '').trim() !== String(row['Siglatura B'] || '').trim()
      ),
      colore: normalizeColorToCanonical(row['Colore'] || 'ORANGE') || 'ORANGE',
      sezione: parseImportedSection(row['Sez [mmq]'], isAWGSection),
      lunghezza: parseInt(row['Lung [mm]']) || 200,
      terminaleA: row['TERM A'] || 'PSW',
      terminaleB: row['TERM B'] || 'PSW',
      collegamentoA: row['COLLEG A'] || '',
      collegamentoB: row['COLLEG B'] || '',
      guaina: sheathId,
      type: 'standard',
      ...(catalogAssignment ? { catalogAssignment } : {})
    });
  });
};

const getSectionColumnHeader = (useAWG = false) => (useAWG ? 'Sez. [AWG]' : 'Sez. [mmq]');

export const importMultipolarCablesData = (worksheet, availableSheaths = [], fileUseAWG = undefined) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
    if (jsonData.length < 2) return [];

    // Costruisce la mappa header → indice colonna (rimuove punti, lowercase)
    // Identico all'approccio di importWiringTableData → funziona con qualsiasi layout
    const norm = (h) => String(h || '').replace(/\./g, '').trim().toLowerCase();
    const headerRow = jsonData[0] || [];
    const col = {};
    headerRow.forEach((h, i) => { const k = norm(h); if (k) col[k] = i; });

    // Indici colonne (undefined se la colonna non esiste nel file)
    const N_COL = col['n'] ?? 0;
    const NOME_COL = col['nome cavo'] ?? undefined;
    const GRUPPO_COL = col['gruppo'] ?? undefined;
    const GUAINA_COL = col['guaina'] ?? undefined;
    const LUNG_BASE = col['lung base [mm]'] ?? undefined;
    const POLO_COL = col['polo'] ?? undefined;
    const COLORE_COL = col['colore'] ?? undefined;
    const SEZ_COL = col['sez [mmq]'] ?? col['sez [awg]'] ?? undefined;
    // Fonte di verità: il toggle "Use AWG" dello sheet Settings; fallback
    // sull'header solo se il foglio Settings manca (vedi importWiringTableData).
    const headerIsAWG = col['sez [mmq]'] === undefined && col['sez [awg]'] !== undefined;
    const isAWGSection = fileUseAWG !== undefined ? fileUseAWG : headerIsAWG;
    const SA_COL = col['sigla a'] ?? undefined;
    const SB_COL = col['sigla b'] ?? undefined;
    const LA_COL = col['lung a [mm]'] ?? undefined;
    const LB_COL = col['lung b [mm]'] ?? undefined;
    const TA_COL = col['term a'] ?? undefined;
    const TB_COL = col['term b'] ?? undefined;
    const CA_COL = col['colleg a'] ?? undefined;
    const CB_COL = col['colleg b'] ?? undefined;
    const INTERNAL_CODE_COL = col['codice interno'] ?? undefined;
    const CATALOG_ID_COL = col['_catalog id'] ?? undefined;

    const get = (row, c, def = '') => c !== undefined ? row[c] ?? def : def;
    const getStr = (row, c, def = '') => String(get(row, c, def) || def);
    const getInt = (row, c) => { const v = get(row, c); return v !== '' && v != null ? parseInt(v) : 0; };

    const rows = jsonData.slice(1).filter(row =>
      row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== '')
    );
    if (rows.length === 0) return [];

    const multipolarCables = [];
    let currentCable = null;

    rows.forEach(row => {
      const numeroCell = row[N_COL];
      const isNewCable = numeroCell && String(numeroCell).startsWith('#');

      if (isNewCable) {
        if (currentCable && currentCable.poli.length > 0) {
          multipolarCables.push({ ...currentCable, type: 'multipolar' });
        }
        const originalIndex = parseInt(String(numeroCell).substring(1)) - 1;

        // Risolvi guaina tramite riferimento (#N) nell'array availableSheaths
        let sheathId = null;
        if (GUAINA_COL !== undefined && availableSheaths.length > 0) {
          const sheathRef = String(row[GUAINA_COL] || '').trim();
          if (sheathRef.startsWith('#')) {
            const idx = parseInt(sheathRef.substring(1)) - 1;
            if (idx >= 0 && idx < availableSheaths.length) sheathId = availableSheaths[idx].id;
          }
        }

        const catalogAssignment = createCatalogAssignment(
          getStr(row, CATALOG_ID_COL),
          getStr(row, INTERNAL_CODE_COL)
        );
        currentCable = {
          originalIndex,
          nomeCavo: getStr(row, NOME_COL),
          gruppo: getStr(row, GRUPPO_COL),
          lunghezza: LUNG_BASE !== undefined ? (parseInt(row[LUNG_BASE]) || 200) : 200,
          guaina: sheathId,
          poli: [],
          ...(catalogAssignment ? { catalogAssignment } : {})
        };
      }

      // Aggiungi polo se la cella "Polo" (es. "P1") è presente
      if (currentCable && POLO_COL !== undefined && row[POLO_COL]) {
        const polo = {
          id: Date.now() + Math.random(),
          colore: normalizeColorToCanonical(getStr(row, COLORE_COL, 'ORANGE')) || 'ORANGE',
          sezione: SEZ_COL !== undefined ? parseImportedSection(row[SEZ_COL], isAWGSection) : 0.5,
          siglaturaA: getStr(row, SA_COL),
          siglaturaB: getStr(row, SB_COL),
          lunghezzaA: getInt(row, LA_COL),
          lunghezzaB: getInt(row, LB_COL),
          terminaleA: getStr(row, TA_COL, 'PSW'),
          terminaleB: getStr(row, TB_COL, 'PSW'),
          collegamentoA: getStr(row, CA_COL),
          collegamentoB: getStr(row, CB_COL)
        };
        currentCable.poli.push(polo);
      }
    });

    if (currentCable && currentCable.poli.length > 0) {
      multipolarCables.push({ ...currentCable, type: 'multipolar' });
    }
    return multipolarCables;
  } catch (error) {
    console.error('Errore nell\'importazione cavi multipolari:', error);
    return [];
  }
};


const importGroupTitles = (worksheet, setGroupTitles) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, {
      header: ['GRUPPO', 'TITOLO', 'NUM_CAVI'],
      range: 1 // Salta la prima riga (header)
    });

    const importedTitles = {};
    jsonData.forEach((row) => {
      if (row.GRUPPO && row.TITOLO && String(row.TITOLO).trim() !== '') {
        importedTitles[row.GRUPPO] = row.TITOLO;
      }
    });

    setGroupTitles(importedTitles);
  } catch (error) {
    console.error('❌ Errore nell\'importazione Group Titles:', error);
  }
};

const importSheathsData = (worksheet) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
    if (jsonData.length < 2) return [];

    const sheaths = [];
    jsonData.slice(1).forEach((row, index) => {
      if (row[0] || row[1] || row[2] || row[3]) {
        sheaths.push({
          id: Date.now() + index, // ID temporaneo (verrà usato l'indice)
          type: row[1] || 'PVC',
          diameter: parseFloat(row[2]) || 6,
          length: parseInt(row[3]) || 300,
          label: `Ø${row[2] || 6} - L${row[3] || 300}`,
          fullLabel: `Guaina ${row[1] || 'PVC'} Ø${row[2] || 6} - L${row[3] || 300}`
        });
      }
    });

    return sheaths;
  } catch (error) {
    console.error('❌ Errore nell\'importazione guaine:', error);
    return [];
  }
};


export const handleFileImport = (event, setCables, setWarnings, setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode, setSuccessMessage, setAvailableSheaths, cableCatalog = {}) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: 'array' });
      let allImportedCables = [];
      let importedSheaths = [];

      // PRIMA: Importa le guaine se presenti
      if (workbook.SheetNames.includes('Sheaths')) {
        importedSheaths = importSheathsData(workbook.Sheets['Sheaths']);
      }
      if (setAvailableSheaths) {
        setAvailableSheaths(importedSheaths);
      }

      let importedSettings = {};
      if (workbook.SheetNames.includes('Settings')) {
        importedSettings = importSettings(workbook.Sheets['Settings'], setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode) || {};
      } else if (typeof setListSortMode === 'function') {
        setListSortMode('numerazione');
      }

      // Fonte di verità per l'unità delle sezioni importate: il toggle del file.
      const fileUseAWG = importedSettings.useAWG;

      if (workbook.SheetNames.includes('Wiring Table')) {
        const standardCables = importWiringTableData(workbook.Sheets['Wiring Table'], importedSheaths, fileUseAWG);
        allImportedCables.push(...standardCables);
      }

      if (workbook.SheetNames.includes('Multipolar Cables')) {
        const multipolarCables = importMultipolarCablesData(workbook.Sheets['Multipolar Cables'], importedSheaths, fileUseAWG);
        allImportedCables.push(...multipolarCables);
      }

      allImportedCables = reconcileCablesWithCatalog(allImportedCables, cableCatalog);
      const unresolvedAssignments = allImportedCables.filter((cable) => cable._catalogStatus === 'unresolved').length;
      const structuralMismatches = allImportedCables.filter((cable) => cable._catalogStatus === 'structure-mismatch').length;

      if (setCables) {
        const sortedCables = allImportedCables
          .sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0))
          .map((cable, index) => ({ ...cable, originalIndex: index }));
        setCables(sortedCables);

        const hasSeparateSingleCableSiglature = sortedCables.some((cable) =>
          !(cable.poli && Array.isArray(cable.poli)) &&
          (
            cable.useSeparateSiglature === true ||
            String(cable.siglaturaA || '').trim() !== String(cable.siglaturaB || '').trim()
          )
        );

        if (hasSeparateSingleCableSiglature) {
          setSettings(prev => ({
            ...prev,
            enableSeparateSingleCableSiglature: true
          }));
        }
      }

      if (workbook.SheetNames.includes('Group Titles')) {
        importGroupTitles(workbook.Sheets['Group Titles'], setGroupTitles);
      } else if (setGroupTitles) {
        setGroupTitles({});
      }

      // Funzione di traduzione (usa 'it' come default poiché settings non è disponibile qui)
      const language = 'it';
      const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
          if (text && typeof text === 'object' && k in text) {
            text = text[k];
          } else {
            return key;
          }
        }
        if (typeof text === 'string' && params) {
          return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
      };

      if (typeof setSuccessMessage === 'function') {
        setSuccessMessage(t('fileOperations.fileImportedSuccess', { name: file.name }));
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setWarnings(prev => [...prev, t('fileOperations.fileImportedSuccess', { name: file.name })]);
      }

      const importedHasSeparateSingleCableSiglature = allImportedCables.some((cable) =>
        !(cable.poli && Array.isArray(cable.poli)) &&
        (
          cable.useSeparateSiglature === true ||
          String(cable.siglaturaA || '').trim() !== String(cable.siglaturaB || '').trim()
        )
      );
      if (importedHasSeparateSingleCableSiglature) {
        setWarnings(prev => [...prev, t('fileOperations.separateSingleCableSiglatureEnabledOnImport')]);
      }
      if (unresolvedAssignments > 0) {
        setWarnings(prev => [...prev, t('fileOperations.unresolvedCatalogAssignments', { count: unresolvedAssignments })]);
      }
      if (structuralMismatches > 0) {
        setWarnings(prev => [...prev, t('fileOperations.catalogStructureMismatches', { count: structuralMismatches })]);
      }
      event.target.value = null;

    } catch (error) {
      console.error("Errore durante l'importazione del file Excel:", error);
      const language = 'it';
      const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
          if (text && typeof text === 'object' && k in text) {
            text = text[k];
          } else {
            return key;
          }
        }
        if (typeof text === 'string' && params) {
          return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
      };
      setWarnings(prev => [...prev, t('fileOperations.importError', { error: error.message })]);
    }
  };
  reader.readAsArrayBuffer(file);
};

// ==================== EXPORT FUNCTIONS ====================

const buildFullFileName = (settings, exportForEW3035, extension) => {
  const rawName = settings.fileName || '';
  const baseName = rawName.trim();
  const withoutExtension = baseName.replace(/\.(xlsx?|pdf)$/i, '');

  const parts = [withoutExtension];

  const revisionNumber = Number.isFinite(settings.revision) ? settings.revision : parseInt(settings.revision) || 0;
  const revisionFormatted = String(revisionNumber).padStart(2, '0');
  parts.push(exportForEW3035 ? `Rev${revisionFormatted}` : `Rev. ${revisionFormatted}`);

  if (settings.listSortMode && settings.listSortMode !== 'numerazione' && !exportForEW3035) {
    parts.push(`[${settings.listSortMode}]`);
  }

  if ((settings.quantitaCablaggi || 1) > 1 || exportForEW3035) {
    const qty = settings.quantitaCablaggi || 1;
    parts.push(exportForEW3035 ? `Q${qty}` : `#${qty}`);
  }

  const separator = exportForEW3035 ? '-' : ' - ';
  const fileName = parts.join(separator);
  return `${fileName}.${extension}`;
};

export const exportToXLSX = async (cables, settings, isSelectionModeEnabled, selectedCables, setWarnings, setSuccessMessage, getSortedCablesForList, getGroupTitle, getCableDisplayName, formatSezione, getTerminalData, exportForEW3035, terminali, availableSheaths = []) => {
  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  const fileName = settings.fileName;
  const fileNameValidation = validateWindowsFileName(fileName);

  if (!fileNameValidation.isValid) {
    setWarnings(prev => [
      ...prev,
      t(fileNameValidation.reason === 'empty'
        ? 'fileOperations.noFileNameWarning'
        : 'fileOperations.invalidWindowsFileNameWarning')
    ]);
    return;
  }

  let cablesToExport = cables;
  if (isSelectionModeEnabled && selectedCables.size > 0) {
    cablesToExport = cables.filter((_, index) => selectedCables.has(index));

    if (cablesToExport.length === 0) {
      setWarnings(prev => [
        ...prev,
        t('fileOperations.noCablesSelectedExport')
      ]);
      return;
    }
  }

  try {
    const workbook = new ExcelJS.Workbook();

    await createWiringTableSheet(workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, availableSheaths);
    await createMultipolarCablesSheet(workbook, cablesToExport, settings, getSortedCablesForList, getGroupTitle, getCableDisplayName, getTerminalData, availableSheaths);
    await createSheathsSheet(workbook, availableSheaths);

    const hasGroups = cablesToExport.some(cable => cable.gruppo && cable.gruppo.trim() !== '');
    const hasGroupTitles = settings.groupTitles && Object.keys(settings.groupTitles).length > 0;

    if (hasGroups || hasGroupTitles) {
      await createGroupTitlesSheet(workbook, cablesToExport, getGroupTitle);
    }

    await createSettingsSheet(workbook, settings, fileName, getGroupTitle);
    await createACPowerSheet(workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, exportForEW3035, terminali, setWarnings);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fullFileName = buildFullFileName(settings, exportForEW3035, 'xlsx');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fullFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Messaggio di successo specifico per esportazione
    const totalCables = cables.length;
    const exportedCables = cablesToExport.length;

    if (isSelectionModeEnabled && selectedCables.size > 0) {
      setSuccessMessage(t('fileOperations.fileExportedSuccessSelected', { name: fullFileName, exported: exportedCables, total: totalCables }));
    } else {
      setSuccessMessage(t('fileOperations.fileExportedSuccessTotal', { name: fullFileName, exported: exportedCables }));
    }
    setTimeout(() => setSuccessMessage(''), 5000);
  } catch (error) {
    console.error('Errore durante l\'esportazione:', error);
    setWarnings(prev => [
      ...prev,
      t('fileOperations.exportError', { error: error.message || error.toString() })
    ]);
  }
};

const createSettingsSheet = async (workbook, settings, fileName, getGroupTitle) => {
  const sheet = workbook.addWorksheet('Settings');

  const settingsData = [
    ['Parameter', 'Value'],
    ['Harness Quantity', settings.quantitaCablaggi],
    ['Mark Cables', settings.marcaCavi],
    ['Order Reference', settings.commessaId || ''],
    ['Order Date', settings.commessaDate || ''],
    ['Revision', settings.revision],
    ['Electrical Schematic', settings.schemaElettrico],
    ['Installation', settings.installazione],
    ['File Name', fileName],
    ['Use AWG', settings.useAWG],
    ['Marking By Connection', !!settings.marcaturePerCollegamento]
  ];

  settingsData.forEach(row => sheet.addRow(row));

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
  });

  sheet.columns.forEach(col => col.width = 22);
};

const createSheathsSheet = async (workbook, availableSheaths) => {
  if (!availableSheaths || availableSheaths.length === 0) {
    return; // Non crea lo sheet se non ci sono guaine
  }

  const sheet = workbook.addWorksheet('Sheaths');
  const headers = ['N.', 'Tipo', 'Diametro [mm]', 'Lunghezza [mm]'];

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;

  availableSheaths.forEach((sheath, index) => {
    sheet.addRow([
      `#${index + 1}`,
      sheath.type || 'PVC',
      sheath.diameter,
      sheath.length
    ]);
  });

  sheet.columns = [
    { width: 6 },      // N.
    { width: 12 },     // Tipo
    { width: 15 },     // Diametro
    { width: 15 }      // Lunghezza
  ];

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF87CEEB' } // Azzurro/sky
    };
  });

  sheet.eachRow((row) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
  });
};

export const createWiringTableSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, availableSheaths = []) => {
  const sheet = workbook.addWorksheet('Wiring Table');

  // Mostra colonna "Guaina" SOLO se ci sono guaine
  const hasSheaths = availableSheaths.length > 0;
  const headers = ['N.', 'Gruppo'];
  if (hasSheaths) headers.push('Guaina');
  headers.push('Siglatura A', 'Siglatura B', 'Colore', getSectionColumnHeader(settings.useAWG), 'Lung. [mm]', 'TERM. A', 'TERM. B', 'COLLEG. A', 'COLLEG. B', 'Codice interno', '_Catalog ID');

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;

  const standardCables = getSortedCablesForList(cablesToExport).filter(cable => !(cable.poli && Array.isArray(cable.poli)));

  standardCables.forEach((cable) => {
    const sheathIndex = (hasSheaths && cable.guaina)
      ? availableSheaths.findIndex(s => s.id === cable.guaina) + 1
      : null;
    const row = [
      `#${cable.originalIndex + 1}`,
      cable.gruppo || ''
    ];
    if (hasSheaths) row.push(sheathIndex ? `#${sheathIndex}` : '');
    const normalizedCable = normalizeStandardCable(cable);
    row.push(
      normalizedCable.siglaturaA || '',
      normalizedCable.siglaturaB || '',
      normalizeColorToCanonical(cable.colore || 'ORANGE') || 'ORANGE',
      settings.useAWG ? convertMetricToAWG(cable.sezione) : cable.sezione,
      cable.lunghezza,
      cable.terminaleA || 'PSW',
      cable.terminaleB || 'PSW',
      cable.collegamentoA || '',
      cable.collegamentoB || '',
      cable.catalogAssignment?.codiceInterno || '',
      cable.catalogAssignment?.itemId || ''
    );
    sheet.addRow(row);
  });

  const columns = [
    { width: 4.71 },   // N.
    { width: 10.71 },  // Gruppo
  ];
  if (hasSheaths) columns.push({ width: 10.71 }); // Guaina
  columns.push(
    { width: 10.71 },  // Siglatura A
    { width: 10.71 },  // Siglatura B
    { width: 15.71 },  // Colore
    { width: 12.71 },  // Sez
    { width: 12.71 },  // Lung
    { width: 10.71 },  // TERM A
    { width: 10.71 },  // TERM B
    { width: 15.71 },  // COLLEG A
    { width: 15.71 },  // COLLEG B
    { width: 18 },     // Codice interno
    { width: 16 }      // Catalog ID
  );
  sheet.columns = columns;
  sheet.getColumn(headers.length).hidden = true;

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow((row) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
  });

  const lastRowWT = sheet.lastRow?.number || 1;
  for (let row = 2; row <= lastRowWT; row++) {
    const cell = sheet.getRow(row).getCell(1);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFF59E' }
    };
  }
};

export const createMultipolarCablesSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getGroupTitle, getCableDisplayName, getTerminalData, availableSheaths = []) => {
  const multipolarCables = getSortedCablesForList(cablesToExport).filter(cable => cable.poli && Array.isArray(cable.poli));

  if (multipolarCables.length === 0) {
    return; // Non crea lo sheet se non ci sono cavi multipolari
  }

  const hasSheaths = availableSheaths.length > 0;

  const sheet = workbook.addWorksheet('Multipolar Cables');
  // Nuovo ordine: N., Gruppo, [Guaina], Nome Cavo, Lung.Base, N°Poli, ...
  const headers = ['N.', 'Gruppo'];
  if (hasSheaths) headers.push('Guaina');
  headers.push(
    'Nome Cavo', 'Lung. Base [mm]', 'N° Poli',
    'Polo', 'Colore', getSectionColumnHeader(settings.useAWG), 'Sigla A', 'Sigla B',
    'Lung. A [mm]', 'Lung. B [mm]', 'TERM. A', 'TERM. B',
    'COLLEG. A', 'COLLEG. B', 'Codice interno', '_Catalog ID'
  );

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;
  let currentRowMP = 2;
  const sortedMulti = multipolarCables.sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0));
  sortedMulti.forEach((cable) => {
    const startRow = currentRowMP;
    const endRow = currentRowMP + cable.poli.length - 1;
    const sheathIndex = (hasSheaths && cable.guaina)
      ? availableSheaths.findIndex(s => s.id === cable.guaina) + 1
      : null;

    cable.poli.forEach((polo, poloIndex) => {
      const isFirst = poloIndex === 0;
      // Nuovo layout: N., Gruppo, [Guaina], Nome Cavo, Lung.Base, N°Poli, Polo, ...
      const rowData = [
        isFirst ? `#${cable.originalIndex + 1}` : '',
        isFirst ? (cable.gruppo || '') : ''
      ];
      if (hasSheaths) {
        rowData.push(isFirst && sheathIndex ? `#${sheathIndex}` : '');
      }
      rowData.push(
        isFirst ? (cable.nomeCavo || '') : '',
        isFirst ? cable.lunghezza : '',
        isFirst ? cable.poli.length : '',
        `P${poloIndex + 1}`,
        normalizeColorToCanonical(polo.colore || 'ORANGE') || 'ORANGE',
        settings.useAWG ? convertMetricToAWG(polo.sezione) : polo.sezione,
        polo.siglaturaA || '',
        polo.siglaturaB || '',
        polo.lunghezzaA,
        polo.lunghezzaB,
        polo.terminaleA || 'PSW',
        polo.terminaleB || 'PSW',
        polo.collegamentoA || '',
        polo.collegamentoB || '',
        isFirst ? (cable.catalogAssignment?.codiceInterno || '') : '',
        isFirst ? (cable.catalogAssignment?.itemId || '') : ''
      );
      sheet.addRow(rowData);
      currentRowMP++;
    });

    // Merge celle per gruppi multi-polo: A=N., B=NomeCavo, C=Gruppo, [D=Guaina], D/E=Lung.Base, E/F=N°Poli
    const mergeColumns = hasSheaths
      ? ['A', 'B', 'C', 'D', 'E', 'F']
      : ['A', 'B', 'C', 'D', 'E'];
    if (cable.poli.length > 1) {
      mergeColumns.forEach(col => {
        sheet.mergeCells(`${col}${startRow}:${col}${endRow}`);
        const cell = sheet.getCell(`${col}${startRow}`);
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
      [headers.length - 1, headers.length].forEach((columnNumber) => {
        sheet.mergeCells(startRow, columnNumber, endRow, columnNumber);
      });
    }
  });

  const columns = [
    { width: 6 },   // N.
    { width: 10 },  // Gruppo
  ];
  if (hasSheaths) {
    columns.push({ width: 10 }); // Guaina
  }
  columns.push(
    { width: 15 },  // Nome Cavo
    { width: 12 },  // Lung. Base
    { width: 8 },   // N° Poli
    { width: 6 },   // Polo
    { width: 12 },  // Colore
    { width: 10 },  // Sez
    { width: 10 },  // Sigla A
    { width: 10 },  // Sigla B
    { width: 10 },  // Lung. A
    { width: 10 },  // Lung. B
    { width: 10 },  // TERM. A
    { width: 10 },  // TERM. B
    { width: 15 },  // COLLEG. A
    { width: 15 },  // COLLEG. B
    { width: 18 },  // Codice interno
    { width: 16 }   // Catalog ID
  );
  sheet.columns = columns;
  sheet.getColumn(headers.length).hidden = true;

  const lastRowMP = sheet.lastRow?.number || 1;
  for (let row = 2; row <= lastRowMP; row++) {
    const cell = sheet.getRow(row).getCell(1);
    if (cell.value && cell.value.toString().startsWith('#')) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFE599' }
      };
    }
  }

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow((row, rowNumber) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
    if (rowNumber > 1) {
      row.getCell(1).font = { bold: true };
    }
  });
};

const createGroupTitlesSheet = async (workbook, cablesToExport, getGroupTitle) => {
  const sheet = workbook.addWorksheet('Group Titles');

  sheet.addRow(['GRUPPO', 'TITOLO', 'NUM_CAVI']);

  const activeGroups = new Set();
  cablesToExport.forEach(cable => {
    if (cable.gruppo && cable.gruppo.trim() !== '') {
      activeGroups.add(cable.gruppo.trim());
    }
  });

  Array.from(activeGroups).sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);

    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    if (!isNaN(numA) && isNaN(numB)) return -1;
    if (isNaN(numA) && !isNaN(numB)) return 1;

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }).forEach(groupName => {
    const cablesInGroup = cablesToExport.filter(c => c.gruppo && c.gruppo.trim() === groupName);
    const title = getGroupTitle(groupName);

    sheet.addRow([
      groupName,
      title || '',
      cablesInGroup.length
    ]);
  });

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  });

  sheet.columns = [
    { width: 15 },
    { width: 40 },
    { width: 12 }
  ];
};

const createACPowerSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, exportForEW3035, terminali, setWarnings) => {
  const sheet = workbook.addWorksheet('AC_Power');

  const headers = [
    'Num.', 'OrderNo.', 'Product', 'WireSize', 'TotalSet', 'T.Finish',
    'Length', 'Len.F1', 'Peel.F1', 'Len.B1', 'Peel.B1', 'Dia1', 'Back1',
    'ClampGapL', 'ClampGapR',
    '', '', '', '', '', '', '', '',  // 8 colonne vuote (P-W)
    'CharsSend', 'WordSize', 'BoldSize',
    'Head1Content', 'FlipH1', 'Head2Content', 'FlipH2', 'MidContent',
    'FlipM', 'Tail2Content', 'FlipT2', 'Tail1Content', 'FlipT1',
    'Printing', 'Head1PrintPos', 'Head2PrintPos', 'PrintLoopType',
    'PrintLoopNum', 'PrintLoopDist', 'Tail2PrintPos', 'Tail1PrintPos'
  ];
  sheet.addRow(headers);

  // Merge delle celle solo per le colonne con header
  for (let col = 1; col <= headers.length; col++) {
    if (headers[col - 1] !== '') {  // Solo se c'è un header
      const colLetter = sheet.getColumn(col).letter;
      sheet.mergeCells(`${colLetter}1:${colLetter}2`);
    }
  }

  const acPowerData = generateACPowerData(
    cablesToExport,
    settings,
    terminali, // Passa l'oggetto terminali corretto
    getSortedCablesForList,
    setWarnings, // Usa la funzione reale setWarnings
    exportForEW3035
  );

  acPowerData.forEach((row, index) => {
    if (exportForEW3035 && index > 0) {
      const prevRow = acPowerData[index - 1];
      const currentRow = row;

      if (prevRow.product !== currentRow.product || prevRow.wireSize !== currentRow.wireSize) {
        const emptyRow = new Array(headers.length).fill('');
        sheet.addRow(emptyRow);
      }
    }

    const rowData = [
      row.num, row.orderNo, row.product, row.wireSize, row.totalSet, row.tFinish,
      row.length, row.lenF1, row.peelF1, row.lenB1, row.peelB1, row.dia1, row.back1,
      row.clampGapL, row.clampGapR,
      row.empty1, row.empty2, row.empty3, row.empty4, row.empty5, row.empty6, row.empty7, row.empty8,
      row.charsSend, row.wordSize, row.boldSize,
      row.head1Content, row.flip1, row.head2Content, row.flip2, row.midContent,
      row.flip3, row.tail2Content, row.flip4, row.tail1Content, row.flip5,
      row.printing, row.head1PrintPos, row.head2PrintPos, row.printLoopType,
      row.printLoopNum, row.printLoopDist, row.tail2PrintPos, row.tail1PrintPos
    ];
    sheet.addRow(rowData);
  });

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
  });

  // Imposta larghezza di default per tutte le colonne
  sheet.columns.forEach(col => col.width = 15);

  // Nascondi completamente le colonne vuote
  for (let i = 16; i <= 23; i++) {
    sheet.getColumn(i).hidden = true;
  }
};

// Funzione helper per generare il nome file completo
export const getFullFileName = (settings, extension = '', includeSort = true, includeQuantity = true, exportForEW3035) => {
  const cleanBaseName = String(settings.fileName || '').trim();
  const withoutExtension = cleanBaseName.replace(/\.(xlsx?|pdf)$/i, '');

  const revisionFormatted = settings.revision.toString().padStart(2, '0');
  const revisionText = exportForEW3035
    ? `Rev${revisionFormatted}`      // Macchinetta: "Rev05"
    : `Rev. ${revisionFormatted}`;   // Normale: "Rev. 05"

  const fileParts = [withoutExtension || cleanBaseName, revisionText];

  if (includeSort && settings.listSortMode !== 'numerazione') {
    const getSortModeDisplayName = (mode) => {
      switch (mode) {
        case 'siglatura': return 'Siglatura';
        case 'sezione': return 'Sezione';
        case 'coloreSezione': return 'Colore/Sezione';
        default: return mode;
      }
    };
    const sortDisplayName = getSortModeDisplayName(settings.listSortMode);
    if (sortDisplayName) {
      fileParts.push(sortDisplayName);
    }
  }

  if ((includeQuantity && settings.quantitaCablaggi > 1) || exportForEW3035) {
    const quantityText = exportForEW3035
      ? `Q${settings.quantitaCablaggi}`           // Macchinetta: sempre "Q1", "Q2", etc.
      : `#${settings.quantitaCablaggi}`;          // Normale: solo se > 1
    fileParts.push(quantityText);
  }

  const separator = exportForEW3035 ? '-' : ' - ';
  const fullName = fileParts.join(separator);

  return extension ? `${fullName}.${extension}` : fullName;
};
